import { trackRetailersPageViewView } from "@faire/web-api/events/retailers/view/pageView";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { getSpacing } from "@faire/web/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { useEffect } from "react";

import { useZustandLocation } from "@faire/retailer/app/_lib/routing/useZustandLocation";
import { Footer } from "@faire/retailer/components/Footer";
import { SlideIn } from "@faire/retailer/components/SlideIn";
import { PageInitialized } from "@faire/retailer/events";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { useEffectAssignWhen } from "@faire/retailer/settings/useEffectAssignWhen";
import { ENABLE_HOMEPAGE_LOCALIZED_CONTENT_241015 } from "@faire/web-api/indigofair/settings/ENABLE_HOMEPAGE_LOCALIZED_CONTENT_241015";
import {
  useAssignSettingHomepageReordering,
  useSettingHomepageReordering,
} from "@faire/retailer/settings/useSettingHomepageReordering";
import { BestsellersBanner } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BestsellersBanner/BestsellersBanner";
import { BlogCarousel } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BlogCarousel/BlogCarousel";
import { BottomSignUpBanner } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BottomSignUpBanner/BottomSignUpBanner";
import { BrandValueBanner } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/BrandValueBanner";
import { CategoryBestBrandsBanner } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/CategoryBestBrandsBanner";
import { TopHeaderBanner } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/TopHeaderBanner/TopHeaderBanner";
import { WeMakeItSimpleBanner } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/WeMakeItSimpleBanner/WeMakeItSimpleBanner";
import { useCategoryBrandsBannerQuery } from "@faire/retailer/views/home/logged-out-homepage/__internal__/DataFetching/useCategoryBrandsBannerQuery";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
export const LoggedOutHomePageBase: React.FC = () => {
  const { isFetching, dataUpdatedAt } = useCategoryBrandsBannerQuery();
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "page-home-logged-out",
    pageLoadKey: "page-home-logged-out-redesign",
    numComponents: 2,
    isPageLoading: isFetching,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
    dataUpdatedAt,
  });
  const { pathname } = useZustandLocation((state) => state.pathname);
  const ref = useDoActionWhenVisible(() =>
    trackRetailersPageViewView(
      "",
      "",
      "",
      "home",
      pathname,
      "page-home-logged-out-redesign"
    )
  );

  const countryCode = getSessionCountry();

  useEffectAssignWhen(
    ENABLE_HOMEPAGE_LOCALIZED_CONTENT_241015,
    countryCode === "FRA" || countryCode === "GBR"
  );

  const assignSettingHomepageReordering = useAssignSettingHomepageReordering();
  const { isReorderVariant } = useSettingHomepageReordering();

  const viewRef = useDoActionWhenVisible(() => {
    assignSettingHomepageReordering();
  });

  useEffect(() => {
    PageInitialized.publish();
  }, []);

  return (
    <div style={{ background: "#FBF8F6", width: "100%" }} ref={ref}>
      <TopHeaderBanner loadTimingRef={loadTimingRef} />
      <CategoryBestBrandsBanner loadTimingRef={loadTimingRef} />
      <SlideIn offset={getSpacing("3x")}>
        <div ref={viewRef}>
          {isReorderVariant ? <WeMakeItSimpleBanner /> : <BestsellersBanner />}
        </div>
      </SlideIn>
      <SlideIn offset={getSpacing("5x")}>
        <BrandValueBanner />
      </SlideIn>
      <SlideIn offset={getSpacing("3x")}>
        {isReorderVariant ? <BestsellersBanner /> : <WeMakeItSimpleBanner />}
      </SlideIn>
      <WhenVisibilityIsImminent>
        <BlogCarousel />
      </WhenVisibilityIsImminent>
      <SlideIn offset={getSpacing("3x")}>
        <BottomSignUpBanner />
      </SlideIn>
      <Footer />
    </div>
  );
};
